/**
 * Configuration Manager for Dynamic Environment Settings
 * Supports runtime configuration and automatic deployment detection
 */

class ConfigManager {
  constructor() {
    this.config = {};
    this.environment = this.detectEnvironment();
    this.configSources = [];
    this.loadedSources = new Set();
    this.basePath = this.detectBasePath();
    this.useRuntimeConfig = true; // Enable runtime configuration
  }

  /**
   * Get runtime configuration (prioritized over static files)
   */
  getRuntimeConfig() {
    if (typeof window !== 'undefined' && window.RUNTIME_CONFIG) {
      return window.RUNTIME_CONFIG;
    }
    return null;
  }

  /**
   * Detect the current environment
   */
  detectEnvironment() {
    // Check runtime config first
    const runtimeConfig = this.getRuntimeConfig();
    if (runtimeConfig?.deployment?.environment) {
      return runtimeConfig.deployment.environment;
    }

    // Check for explicit environment variables
    if (process.env.REACT_APP_ENV) {
      return process.env.REACT_APP_ENV;
    }

    // Check for NODE_ENV
    if (process.env.NODE_ENV === 'production') {
      return 'production';
    }

    if (process.env.NODE_ENV === 'development') {
      return 'development';
    }

    // Check hostname for environment detection
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;

      if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
        return 'development';
      }

      if (hostname.includes('staging') || hostname.includes('test')) {
        return 'staging';
      }

      if (hostname.includes('prod') || hostname.endsWith('.com')) {
        return 'production';
      }
    }

    return 'development';
  }

  /**
   * Detect the current deployment base path
   */
  detectBasePath() {
    // Check runtime config first (auto-detected during runtime)
    const runtimeConfig = this.getRuntimeConfig();
    if (runtimeConfig?.deployment?.basePath) {
      return runtimeConfig.deployment.basePath;
    }

    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;

      // If pathname is just '/' or empty, we're at root
      if (!pathname || pathname === '/') {
        return '';
      }

      // Check if we're in a known subdirectory structure
      // Look for common deployment patterns
      const segments = pathname.split('/').filter(Boolean);

      if (segments.length > 0) {
        const firstSegment = segments[0];

        // Common patterns for deployed React apps
        const knownPatterns = [
          'fiq-pro', 'app', 'web', 'dashboard', 'admin',
          'footwear', 'gallery', 'client', 'ui',
        ];

        // If first segment matches a known pattern, use it
        if (knownPatterns.some(pattern => firstSegment.toLowerCase().includes(pattern))) {
          return `/${firstSegment}/`; // Always include trailing slash
        }

        // If there are multiple segments, assume first is the app base
        if (segments.length >= 2) {
          return `/${firstSegment}/`; // Always include trailing slash
        }
      }

      // Check if there's a PUBLIC_URL environment variable
      if (process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '.') {
        const url = process.env.PUBLIC_URL.startsWith('/') ? process.env.PUBLIC_URL : `/${process.env.PUBLIC_URL}`;
        return url.endsWith('/') ? url : `${url}/`; // Ensure trailing slash
      }
    }

    return '';
  }

  /**
   * Add a configuration source
   */
  addSource(name, loader) {
    this.configSources.push({ name, loader });
  }

  /**
   * Load configuration with runtime priority
   */
  async loadConfig() {
    console.log(`Loading configuration for environment: ${this.environment}`);


    // Load base configuration
    this.config = this.getBaseConfig();

    // Check for runtime configuration first (highest priority)
    const runtimeConfig = this.getRuntimeConfig();
    if (runtimeConfig) {
      console.log('✅ Using runtime configuration (portable deployment)');
      this.config = this.deepMerge(this.config, runtimeConfig);
      this.loadedSources.add('runtime-config');
    } else {
      console.log('📁 Loading static configuration files...');
      // Load from static sources as fallback
      for (const source of this.configSources) {
        try {
          console.log(`Loading config from source: ${source.name}`);
          const sourceConfig = await source.loader(this.environment);
          if (sourceConfig) {
            this.mergeConfig(sourceConfig);
            this.loadedSources.add(source.name);
            console.log(`Successfully loaded config from: ${source.name}`);
          }
        } catch (error) {
          console.warn(`Failed to load config from ${source.name}:`, error);
        }
      }
    }

    // Apply environment variables
    this.applyEnvironmentVariables();

    // Validate configuration
    this.validateConfig();

    console.log('Configuration loaded successfully:', {
      environment: this.environment,
      basePath: this.basePath,
      sources: Array.from(this.loadedSources),
      api: { domain: this.config.api?.domain },
      aliasPrefix: this.config.deployment?.aliasPrefix,
    });

    return this.config;
  }

  /**
   * Get base configuration
   */
  getBaseConfig() {
    return {
      api: {
        domain: 'https://sandboxfwapi.evidenceiq.com',
        port: '',
        timeout: 30000,
        retryAttempts: 3,
        googleMapsApiKey: 'AIzaSyBYOgqZ4JqRxMjxl6erXprF_JgBgaMvnU0'
      },
      images3: {
        link: 'https://fwiq-sb.s3.us-east-2.amazonaws.com',
        region: 'us-east-2',
        bucket: 'fwiq-sb'
      },
      app: {
        name: 'Footwear Intelligence',
        version: '1.0.0',
        debug: false,
        enableLogging: true,
        maxFileSize: 10 * 1024 * 1024, // 10MB
        supportedImageTypes: ['image/jpeg', 'image/png', 'image/webp'],
        pageSize: 50,
        maxRetries: 3
      },
      ui: {
        theme: 'light',
        language: 'en',
        dateFormat: 'MM/DD/YYYY',
        timeFormat: '12h',
        currency: 'USD',
        enableAnimations: true,
        showDebugInfo: false
      },
      features: {
        enableAdvancedSearch: true,
        enableImageEditing: true,
        enableBulkOperations: true,
        enableExport: true,
        enableNotifications: true,
        enableOfflineMode: false
      },
      security: {
        tokenRefreshInterval: 15 * 60 * 1000, // 15 minutes
        sessionTimeout: 8 * 60 * 60 * 1000, // 8 hours
        requireHttps: true,
        enableCORS: true
      },
      Defines: {
        ImageSize: {
          full: 'full',
          thumb: 'thumb',
          small: 'small',
          medium: 'medium',
          large: 'large',
        },
        GalleryType: {
          catalog: 'catalog',
          evidence: 'evidence',
        },
        CategoryID: {
          NewShoe: 1,
          RecoveredShoe: 2,
          TestShoe: 3
        },
        CategoryName: {
          NewShoe: 'New Shoe',
          RecoveredShoe: 'Recovered Impression',
          TestShoe: 'Test Shoe'
        },
        ImportStatus:
        {
          Unknown: -1,
          Pending: 0,
          HasInformation: 1,
          HasOriginalImages: 2,
          Standardizing: 3,
          HasStandardizedImages: 4,
          HasVector: 8,
        },
        ImageType: {
          SHOE_SOLE: 1,
          SHOE_SOLE_LEFT: 101,
          SHOE_SOLE_RIGHT: 102,
          SHOE_SOLE_UNDETERMINED: 103,
          SHOE_PRINT: 2,
          SHOE_PRINT_LEFT: 201,
          SHOE_PRINT_RIGHT: 202,
          SHOE_PRINT_UNDETERMINED: 203,
          SHOE_OTHER: 3,
        },
        ImageTypeKey: {
          SHOE_SOLE: 'Sole',
          SHOE_SOLE_LEFT: 'SoleLeft',
          SHOE_SOLE_RIGHT: 'SoleRight',
          SHOE_SOLE_UNDETERMINED: 'SoleUndetermine',
          SHOE_PRINT: 'Print',
          SHOE_PRINT_LEFT: 'PrintLeft',
          SHOE_PRINT_RIGHT: 'PrintRight',
          SHOE_PRINT_UNDETERMINED: 'PrintUndetermine',
          SHOE_OTHER: 'Other',
        },
        ImageTypeName: {
          SHOE_SOLE: 'Shoe Sole',
          SHOE_SOLE_LEFT: 'Sole Left',
          SHOE_SOLE_RIGHT: 'Sole Right',
          SHOE_SOLE_UNDETERMINED: 'Sole Undetermined',
          SHOE_PRINT: 'Shoe Print',
          SHOE_PRINT_LEFT: 'Print Left',
          SHOE_PRINT_RIGHT: 'Print Right',
          SHOE_PRINT_UNDETERMINED: 'Print Undetermined',
          SHOE_OTHER: 'Other',
        },
      }
    };
  }

  /**
   * Merge configuration objects
   */
  mergeConfig(newConfig) {
    this.config = this.deepMerge(this.config, newConfig);
  }

  /**
   * Deep merge two objects
   */
  deepMerge(target, source) {
    const result = { ...target };

    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = this.deepMerge(result[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }

    return result;
  }

  /**
   * Apply environment variables to configuration
   */
  applyEnvironmentVariables() {
    // API Configuration
    if (process.env.REACT_APP_API_DOMAIN) {
      this.config.api.domain = process.env.REACT_APP_API_DOMAIN;
    }

    if (process.env.REACT_APP_API_PORT) {
      this.config.api.port = process.env.REACT_APP_API_PORT;
    }

    if (process.env.REACT_APP_API_TIMEOUT) {
      this.config.api.timeout = parseInt(process.env.REACT_APP_API_TIMEOUT);
    }

    // S3 Configuration
    if (process.env.REACT_APP_S3_BUCKET) {
      this.config.images3.link = `https://${process.env.REACT_APP_S3_BUCKET}.s3.${process.env.REACT_APP_S3_REGION || 'us-east-2'}.amazonaws.com`;
      this.config.images3.bucket = process.env.REACT_APP_S3_BUCKET;
    }

    if (process.env.REACT_APP_S3_REGION) {
      this.config.images3.region = process.env.REACT_APP_S3_REGION;
    }

    // App Configuration
    if (process.env.REACT_APP_DEBUG === 'true') {
      this.config.app.debug = true;
      this.config.ui.showDebugInfo = true;
    }

    if (process.env.REACT_APP_VERSION) {
      this.config.app.version = process.env.REACT_APP_VERSION;
    }

    // Google Maps API Key
    if (process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
      this.config.api.googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    }
  }

  /**
   * Validate configuration
   */
  validateConfig() {
    const errors = [];

    // Validate required fields
    if (!this.config.api?.domain) {
      errors.push('API domain is required');
    }

    if (!this.config.images3?.link) {
      errors.push('S3 images link is required');
    }

    // Validate URLs
    try {
      new URL(this.config.api.domain);
    } catch {
      errors.push('Invalid API domain URL');
    }

    try {
      new URL(this.config.images3.link);
    } catch {
      errors.push('Invalid S3 images URL');
    }

    if (errors.length > 0) {
      console.error('Configuration validation errors:', errors);
      throw new Error(`Configuration validation failed: ${errors.join(', ')}`);
    }
  }

  /**
   * Get configuration value
   */
  get(path, defaultValue = undefined) {
    return path.split('.').reduce((obj, key) => obj?.[key], this.config) ?? defaultValue;
  }

  /**
   * Set configuration value
   */
  set(path, value) {
    const keys = path.split('.');
    const lastKey = keys.pop();
    const target = keys.reduce((obj, key) => {
      if (!obj[key]) obj[key] = {};
      return obj[key];
    }, this.config);
    target[lastKey] = value;
  }

  /**
   * Get current environment
   */
  getEnvironment() {
    return this.environment;
  }

  /**
   * Get current base path
   */
  getBasePath() {
    return this.basePath;
  }

  /**
   * Get full configuration
   */
  getConfig() {
    return this.config;
  }

  /**
   * Check if running in production
   */
  isProduction() {
    return this.environment === 'production';
  }

  /**
   * Check if running in development
   */
  isDevelopment() {
    return this.environment === 'development';
  }

  /**
   * Check if running in staging
   */
  isStaging() {
    return this.environment === 'staging';
  }
}

export default ConfigManager;
